/**
 * apiSpecParser.js
 * Deterministic (no AI, no network) parser that turns whatever the user pastes
 * (cURL, "POST https://..." + headers + JSON, a JSON descriptor, or a bare URL)
 * into one normalized spec:
 *   { method, url, baseUrl, query, headers, body, bodyJson, contentType }
 * Secrets never leave the browser because of this step.
 */

const METHODS = ["GET", "POST", "PUT", "PATCH", "DELETE"];

/** Shell-like split that respects '...' and "..." quoting. */
const tokenize = (s) => {
  const out = [];
  let cur = "";
  let quote = null;
  let has = false;
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (quote) {
      if (c === quote) quote = null;
      else if (c === "\\" && quote === '"' && i + 1 < s.length) cur += s[++i];
      else cur += c;
    } else if (c === '"' || c === "'") {
      quote = c;
      has = true;
    } else if (/\s/.test(c)) {
      if (cur || has) {
        out.push(cur);
        cur = "";
        has = false;
      }
    } else cur += c;
  }
  if (cur || has) out.push(cur);
  return out;
};

const safeJson = (s) => {
  try {
    const v = JSON.parse(s);
    return v && typeof v === "object" ? v : null;
  } catch {
    return null;
  }
};

/** Split URL into baseUrl + query without URL-encoding {{placeholders}}. */
const splitUrl = (raw) => {
  const url = (raw || "").trim();
  const q = url.indexOf("?");
  const baseUrl = q === -1 ? url : url.slice(0, q);
  const query = {};
  if (q !== -1) {
    url
      .slice(q + 1)
      .split("&")
      .filter(Boolean)
      .forEach((pair) => {
        const eq = pair.indexOf("=");
        const k = eq === -1 ? pair : pair.slice(0, eq);
        const v = eq === -1 ? "" : pair.slice(eq + 1);
        try {
          query[decodeURIComponent(k)] = decodeURIComponent(v.replace(/\+/g, " "));
        } catch {
          query[k] = v;
        }
      });
  }
  return { baseUrl, query };
};

const finalize = ({ method, url, headers = {}, body = null, query = {} }) => {
  const { baseUrl, query: urlQuery } = splitUrl(url);
  if (!/^https?:\/\//i.test(baseUrl)) return null;

  const h = {};
  Object.entries(headers).forEach(([k, v]) => (h[String(k).trim()] = String(v).trim()));
  const ctKey = Object.keys(h).find((k) => k.toLowerCase() === "content-type");
  const bodyStr = body == null ? null : typeof body === "string" ? body : JSON.stringify(body, null, 2);
  const bodyJson = typeof body === "object" && body ? body : bodyStr ? safeJson(bodyStr) : null;

  let m = (method || "").toUpperCase();
  if (!METHODS.includes(m)) m = bodyStr ? "POST" : "GET";

  return {
    method: m,
    url,
    baseUrl,
    query: { ...urlQuery, ...query },
    headers: h,
    body: bodyStr,
    bodyJson,
    contentType: ctKey ? h[ctKey] : bodyJson ? "application/json" : null,
  };
};

const parseCurl = (text) => {
  const tokens = tokenize(text.replace(/\\\r?\n/g, " ").replace(/\^\r?\n/g, " "));
  let method = null, url = null, getMode = false;
  const headers = {};
  const data = [];
  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i];
    const next = () => tokens[++i];
    if (t === "-X" || t === "--request") method = next();
    else if (t === "-H" || t === "--header") {
      const h = next() || "";
      const idx = h.indexOf(":");
      if (idx > 0) headers[h.slice(0, idx).trim()] = h.slice(idx + 1).trim();
    } else if (/^(-d|--data|--data-raw|--data-binary|--data-ascii|--data-urlencode)$/.test(t)) data.push(next());
    else if (t === "-G" || t === "--get") getMode = true;
    else if (t === "-u" || t === "--user") headers.Authorization = `Basic ${typeof btoa === "function" ? btoa(next() || "") : next()}`;
    else if (t === "--url") url = next();
    else if (/^https?:\/\//i.test(t)) url = url || t;
  }
  if (!url) return null;
  let body = data.length ? data.join("&") : null;
  const query = {};
  if (getMode && body) {
    body.split("&").forEach((p) => {
      const [k, v = ""] = p.split("=");
      query[k] = v;
    });
    body = null;
  }
  return finalize({ method: method || (body ? "POST" : "GET"), url, headers, body, query });
};

const parseLoose = (text) => {
  const lines = text.split(/\r?\n/);
  let method = null, url = null;
  const headers = {};
  for (const line of lines) {
    const m = line.trim().match(/^(GET|POST|PUT|PATCH|DELETE)\s+(https?:\/\/\S+)/i);
    if (m && !url) {
      method = m[1];
      url = m[2];
      continue;
    }
    const h = line.trim().match(/^([A-Za-z][\w-]*)\s*:\s*(.+)$/);
    if (h && !/^https?$/i.test(h[1]) && !/^["{]/.test(line.trim())) headers[h[1]] = h[2];
  }
  if (!url) url = (text.match(/https?:\/\/[^\s"'<>]+/) || [])[0];
  const a = text.indexOf("{"), b = text.lastIndexOf("}");
  const body = a !== -1 && b > a ? text.slice(a, b + 1) : null;
  return url ? finalize({ method, url, headers, body: safeJson(body || "") ? body : null }) : null;
};

const parseDescriptor = (obj) => {
  const g = (...ks) => ks.map((k) => obj[k]).find((v) => v != null);
  const url = g("url", "endpoint", "baseUrl", "base_url");
  if (!url) return null;
  return finalize({
    method: g("method", "httpMethod"),
    url,
    headers: g("headers") || {},
    body: g("body", "payload", "data") ?? null,
    query: g("params", "query", "queryParams") || {},
  });
};

/** True only for an unmistakable paste (cURL, "POST https://...", or a JSON descriptor),
 *  so ordinary questions that merely contain a URL still go to normal chat. */
export const isExplicitApiPaste = (t = "") =>
  /^\s*(curl\b|(GET|POST|PUT|PATCH|DELETE)\s+https?:\/\/)/i.test(t) ||
  (/^\s*\{/.test(t) && /"(url|endpoint)"/i.test(t));

/** @returns {object|null} normalized spec, or null if nothing usable was found */
export const parseApiInput = (text = "") => {
  const t = text.trim();
  if (!t) return null;
  const json = safeJson(t);
  if (json && !Array.isArray(json)) return parseDescriptor(json);
  if (/^curl\b/i.test(t)) return parseCurl(t);
  return parseLoose(t);
};
